import { v4 as uuidv4 } from 'uuid';
import { TranscriptContainer } from '@src/transcript/models/TranscriptContainer';
import { Segment } from '@src/transcript/models/Segment';
import { Paragraph } from '@src/transcript/models/Paragraph';
import { TextElement, PunctElement } from '@src/transcript/models/TranscriptElement';
import { ITranscriptElement, ITextElement } from '@src/transcript/models/interfaces';
import { TranscriptEditorObserverManager } from '@src/transcript/managers/TranscriptEditorObserverManager';
import { TranscriptEditorCommandManager } from '@src/transcript/managers/TranscriptEditorCommandManager';
import { SplitParagraphCommand } from '@src/transcript/commands/SplitParagraphCommand';
import { MergeParagraphsCommand } from '@src/transcript/commands/MergeParagraphsCommand';
import { MoveParagraphToSegmentCommand } from '@src/transcript/commands/MoveParagraphToSegmentCommand';
import { DeleteParagraphCommand } from '@src/transcript/commands/DeleteParagraphCommand';
import { DeleteElementCommand } from '@src/transcript/commands/DeleteElementCommand';
import { CreateSegmentFromParagraphCommand } from '@src/transcript/commands/CreateSegmentFromParagraphCommand';
import { UpdateParagraphElementsCommand } from '@src/transcript/commands/UpdateParagraphElementsCommand';
import { UpdateElementTextCommand } from '@src/transcript/commands/UpdateElementTextCommand';
import { ProcessAlignmentResultCommand } from '@src/transcript/commands/ProcessAlignmentResultCommand';
import { 
  TranscriptEditorChangeType, 
  TranscriptEditorObserver,
  TranscriptVoiceMapping,
  EditOperation,
  PendingTextEdit,
  UntimingText
} from './types';
import { SpeakerService } from './services/SpeakerService';
import { TimelineService } from './services/TimelineService';
import { SelectionService } from './services/SelectionService';
import { AudioService } from './services/AudioService';
import { getApiUrl } from './utils/api';
import WaveformData from 'waveform-data';
// Import command dynamically to avoid circular dependencies
const { AssignSpeakerToSelectedTextCommand } = require('./commands/AssignSpeakerToSelectedTextCommand');
const { AssignSpeakerToSegmentCommand } = require('./commands/AssignSpeakerToSegmentCommand');
const { TranscriptAssignColorCommand } = require('./commands/TranscriptAssignColorCommand');
const { TranscriptClearColorCommand } = require('./commands/TranscriptClearColorCommand');
import { TranscriptData, TranscriptElement } from '@src/types.ts/transcript';
import { LegacyConverter } from './utils/LegacyConverter';
// Initialize global debugging object for alignment

declare global {
  interface Window {
    _arborAlignmentDebug?: any;
  }
}

// Create the global debug object if it doesn't exist
if (typeof window !== 'undefined') {
  window._arborAlignmentDebug = window._arborAlignmentDebug || {};
}

/**
 * TranscriptEditorCore - A headless editor for transcripts
 * 
 * This class manages the loading, editing, and persistence of transcripts
 * using the data model with segments and paragraphs.
 */
export class TranscriptEditorCore {
  /** Story ID for the transcript */
  storyId: string;
  
  /** Album ID for the transcript */
  albumId: string;
  
  /** Language code for the current transcript */
  private language: string;
  
  /** The main transcript container */
  private container: TranscriptContainer | null = null;
  
  /** Original container state for detecting changes */
  private originalContainer: TranscriptContainer | null = null;
  
  /**
   * Check if there are pending changes that need to be saved
   * @returns true if there are pending operations, false otherwise
   */
  hasPendingChanges(): boolean { 
    return this.pendingOperations.length > 0;
  }
  
  /**
   * Gets the number of pending operations awaiting save
   * @returns The count of pending operations
   */
  getPendingOperationsCount(): number {
    return this.pendingOperations.length;
  }
  
  /** Services are now directly managed by the editor */
  
  /** Speaker service for managing speakers */
  speakerService: SpeakerService | null = null;
  selectionService: SelectionService | null = null;
  timelineService: TimelineService | null = null;
  audioService: AudioService | null = null;
  
  /** Pending edit operations that need to be saved */
  private pendingOperations: EditOperation[] = [];
  
  /** Waveform data for audio visualization */
  private waveformData: WaveformData | null = null;
  
  private audioUrl: string;

  private transcription: TranscriptData | null = null;

  private originalTranscription: TranscriptData | null = null;

  /** Loading state */
  private isLoading: boolean = false;
  
  /** Error message */
  private errorMessage: string | null = null;
  
  /** Observer pattern for notifications */
  private transcriptEditorObserverManager: TranscriptEditorObserverManager;
  
  /** Command manager for undo/redo */
  private _commandManager: TranscriptEditorCommandManager;
  
  /**
   * Gets the command manager for this editor
   * Provides access to undo/redo and command execution functionality
   * @returns The command manager instance
   */
  get commandManager(): TranscriptEditorCommandManager {
    return this._commandManager;
  }
  
  /** Flag for batching operations */
  private inBatch: boolean = false;
  
  /** Count of nested batch operations */
  private batchDepth: number = 0;
  
  /** Pending text edits for debouncing */
  private pendingTextEdits: Map<string, PendingTextEdit> = new Map();
  
  /** Timeout for debouncing text edits */
  private textEditTimeout: number | null = null;
  
  /** Text elements without timing data that need to be processed */
  private untimedTextElements: UntimingText[] = [];
  
  /**
   * Create a new TranscriptEditorCore instance
   * 
   * @param storyId The ID of the story to edit transcript for
   * @param language The language code (defaults to 'en-us')
   */
  constructor(storyId: string, albumId: string, audioUrl: string, waveformData: WaveformData | null, transcription: TranscriptData, language: string = 'en-us') {
    this.storyId = storyId;
    this.albumId = albumId;
    this.language = language;
    this.audioUrl = audioUrl;
    this.waveformData = waveformData;
    this.transcription = transcription;

    // Initialize managers
    this.transcriptEditorObserverManager = new TranscriptEditorObserverManager(this as any);
    this._commandManager = new TranscriptEditorCommandManager();
    // Wire up auto-save: let the CommandManager call saveChanges() on this editor
    this.commandManager.setEditor(this);
    
  }
  
  /**
   * Initialize the editor by loading resources and transcript data
   * 
   * @param albumId The album ID for the transcript
   * @returns True if initialization was successful
   */
  async initialize(albumId: string): Promise<boolean> {
    try {
      this.isLoading = true;
      this.errorMessage = null;
      
      // Set album ID
      this.albumId = albumId;
      
      console.log('Initializing transcript editor');
    
      this.loadContainer();

      this.isLoading = false;
      
      // Notify observers that transcript is loaded
      this.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_LOADED, {
        container: this.container,
        language: this.language,
      });
      
      return true;
    } catch (error) {
      console.error('Error initializing transcript editor:', error);
      this.errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      this.isLoading = false;
      
      // Notify observers about error
      this.notifyObservers(TranscriptEditorChangeType.ERROR_OCCURRED, {
        error: this.errorMessage
      });
      
      return false;
    }
  }

  setLanguage(language: string): void {
    this.language = language;
  }

  getLanguage(): string {
    return this.language;
  }

  setStoryId(storyId: string): void {
    this.storyId = storyId;
  }

  getStoryId(): string {
    return this.storyId;
  }

  setAlbumId(albumId: string): void {
    this.albumId = albumId;
  }

  getAlbumId(): string {
    return this.albumId;
  }

  setAudioUrl(audioUrl: string): void {
    this.audioUrl = audioUrl;
  }

  getAudioUrl(): string {
    return this.audioUrl;
  }

  setTranscription(transcription: TranscriptData | null): void {
    this.transcription = transcription;    
    this.loadContainer();
  }

  getTranscription(): TranscriptData | null {
    return this.transcription;
  }

  setWaveformData(waveformData: WaveformData | null): void {
    this.waveformData = waveformData;
  }

  loadContainer(): void {
    if (!this.transcription || !this.transcription.elements) {
      // Create an empty container if no transcription
      this.container = new TranscriptContainer(uuidv4(), this.storyId, 0, this.language);
      return;
    }
    
    // Use the adapter to convert elements to the expected format
    const adaptedElements = LegacyConverter.adaptTranscriptElements(this.transcription.elements);
    
    // Now pass the adapted elements to the conversion method
    this.container = LegacyConverter.legacyTranscriptToContainer(
      adaptedElements,
      this.transcription.version || 0, 
      this.storyId, 
      this.language
    );
  }

  
  // loadSpeakerMappings
  async loadSpeakerMappings(): Promise<void> {
    try {
      const url = getApiUrl(`/api/story/${this.storyId}/transcript-data?resource=speaker-mappings&storyId=${this.storyId}`);
      const response = await fetch(url);

      if (response.ok) {  
        const data = await response.json();
        this.speakerService?.loadSpeakerMappings(data.data);
        
        // Notify observers that speaker mappings were updated
        this.notifyObservers(TranscriptEditorChangeType.SPEAKER_MAPPINGS_LOADED, {
          mappings: data.data
        });
        
        // Force redraw of container with updated speaker data
        if (this.container) {
          this.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_LOADED, {
            container: this.container,
            language: this.language,
          });
        }
      }
    } catch (error) {
      console.error('Error loading speaker mappings:', error);
    }
  }
  
  /**
   * Register an observer for transcript editor changes
   * 
   * @param observer The observer to register
   */
  registerObserver(observer: TranscriptEditorObserver): void {
    this.transcriptEditorObserverManager.registerObserver(observer);
  }
  
  /**
   * Removes an observer from receiving notifications
   * @param observer The observer to remove
   */
  removeObserver(observer: TranscriptEditorObserver): void {
    this.transcriptEditorObserverManager.unregisterObserver(observer);
  }
  
  /**
   * Unregister an observer
   * 
   * @param observer The observer to unregister
   */
  unregisterObserver(observer: TranscriptEditorObserver): void {
    this.transcriptEditorObserverManager.unregisterObserver(observer);
  }
  
  /**
   * Notify all observers of a change
   * 
   * @param changeType Type of change that occurred
   * @param payload Data associated with the change
   */
  notifyObservers(changeType: TranscriptEditorChangeType, payload: Record<string, any> = {}): void {
    // Check if this notification should be delivered immediately using special flag
    const forceImmediate = payload._forceImmediate === true;
    
    // Remove internal flags before passing to observers
    if (payload._forceImmediate !== undefined) {
      const { _forceImmediate, ...rest } = payload;
      payload = rest;
    }
    
    if (this.inBatch && !forceImmediate) {
      // Defer notification until end of batch
      return;
    }
    
    this.transcriptEditorObserverManager.notifyObservers(changeType, payload, forceImmediate);
  }
  
  /**
   * Begin a batch of operations
   * This prevents observer notifications until endBatch is called
   */
  beginBatch(): void {
    this.inBatch = true;
    this.batchDepth++;
  }
  
  /**
   * End a batch of operations
   * This will trigger a notification to observers
   */
  endBatch(): void {
    this.batchDepth--;
    
    if (this.batchDepth === 0) {
      this.inBatch = false;
      
      // Process any queued notifications first
      this.transcriptEditorObserverManager.processQueuedNotifications();
      
      // Notify observers of batch completion
      this.notifyObservers(TranscriptEditorChangeType.BATCH_COMPLETED, {
        container: this.container
      });
      
      // Always send a TRANSCRIPT_CHANGED event to ensure UI updates
      this.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_CHANGED, {
        container: this.container
      });
    }
  }
  
  /**
   * Execute a function within a batch operation
   * 
   * @param operation Function to execute in batch
   * @returns Result of the operation
   */
  executeInBatch<T>(operation: () => T): T {
    this.beginBatch();
    try {
      return operation();
    } finally {
      this.endBatch();
    }
  }
  
  /**
   * Execute an async function within a batch operation
   * 
   * @param callback Async function to execute in batch
   */
  async withBatch(callback: () => Promise<void> | void): Promise<void> {
    this.beginBatch();
    try {
      await callback();
    } finally {
      this.endBatch();
    }
  }
  
  /**
   * Returns the current batch depth
   * Exposed for CommandManager to use
   */
    getBatchDepth(): number {
      return this.batchDepth;
    }
  
    /**
     * Returns whether we're currently in a batch operation
     * Replacement for getBatchDepth() > 0 checks
     */
    isInBatch(): boolean {
      return this.batchDepth > 0;
    }


  // loadOriginalTranscription similar to fetchSpeakerMappings
  async loadOriginalTranscription(language?: string): Promise<void> {
    try {
      const url = getApiUrl(`/api/story/${this.storyId}/transcript-data?resource=original-transcription&storyId=${this.storyId}`);
      const response = await fetch(url);

      if (response.ok) {
        const data = await response.json();
        this.originalTranscription = data.data;
        
        if (this.originalTranscription?.elements) {
          // Use the adapter to convert elements to the expected format
          const adaptedElements = LegacyConverter.adaptTranscriptElements(this.originalTranscription.elements);
          
          // Now pass the adapted elements to the conversion method
          this.originalContainer = LegacyConverter.legacyTranscriptToContainer(
            adaptedElements,
            0, 
            this.storyId, 
            this.language
          );
        }
      }
    } catch (error) {
      console.error('Error loading original transcription:', error);
    }
  }
  
  getOriginalTranscription(): TranscriptData | null {
    return this.originalTranscription;
  }

  async getOriginalContainer(): Promise<TranscriptContainer | null> {
    if (!this.originalContainer) {
      await this.loadOriginalTranscription();
    }
    return this.originalContainer;
  }


  /**
   * Save changes to transcript data
   * 
   * @returns True if changes were saved successfully
   */
  async saveChanges(): Promise<boolean> {
    try {
      if (!this.container) {
        throw new Error('No transcript container available');
      }
      

      // Get transcript in legacy format
      const legacyTranscript = LegacyConverter.containerToLegacyTranscript(this.container);
      legacyTranscript.version = this.container.version + 1;
      
      // Convert to app-wide TranscriptData format
      const transcription = LegacyConverter.adaptToTranscriptData(legacyTranscript);
      
      // save the transcription
      this.saveTranscription(transcription);
      
      // Clear pending operations
      this.pendingOperations = [];
      
      console.log('Save operation completed successfully');
      
      // Notify observers that changes were saved
      this.notifyObservers(TranscriptEditorChangeType.CHANGES_SAVED, {
        container: this.container
      });
      
      return true;
    } catch (error) {
      console.error('Error saving changes:', error);
      
      // Notify observers of error
      this.notifyObservers(TranscriptEditorChangeType.ERROR_OCCURRED, {
        error: error instanceof Error ? error.message : 'Unknown error during save'
      });
      
      return false;
    }
  }
  
  /**
   * Deletes a segment from the container
   * @param segmentId The ID of the segment to delete
   */
  private deleteSegment(segmentId: string): void {
    if (!this.container) {
      return;
    }
    this.container.segments = this.container.segments.filter(segment => segment.id !== segmentId);
  }

  /**
   * Deletes a paragraph from the container
   * @param paragraphId The ID of the paragraph to delete
   */
  private forceDeleteParagraph(paragraphId: string): void {
    if (!this.container) {
      return;
    }
    // find the segment that contains the paragraph
    const segment = this.container.getSegment(paragraphId);
    if (segment) {
      segment.paragraphs = segment.paragraphs.filter(paragraph => paragraph.id !== paragraphId);
    }
  }

  /**
   * Validates and optimizes the container structure
   * This removes empty segments/paragraphs and combines adjacent segments with the same speaker
   * @param performMerge Whether to perform segment merging (defaults to true)
   * @returns Validation result with success flag, reason if invalid, and statistics about changes made
   */
  validateAndOptimizeContainer(performMerge: boolean = true): { 
    valid: boolean; 
    reason?: string;
    stats: {
      emptySegmentsRemoved: number;
      emptyParagraphsRemoved: number;
      segmentsMerged: number;
      elementsReassigned: number;
    }
  } {
    const stats = {
      emptySegmentsRemoved: 0,
      emptyParagraphsRemoved: 0,
      segmentsMerged: 0,
      elementsReassigned: 0
    };

    if (!this.container) {
      return { valid: false, reason: 'No container available', stats };
    }
    
    // Check if container has segments
    if (!this.container.segments || this.container.segments.length === 0) {
      return { valid: false, reason: 'Container has no segments', stats };
    }
    
    // First, remove empty paragraphs from each segment
    for (const segment of [...this.container.segments]) {
      // Check if segment has valid ID
      if (!segment.id) {
        return { valid: false, reason: 'Segment has no ID', stats };
      }
      
      // First, clean up invalid paragraphs
      if (segment.paragraphs && segment.paragraphs.length > 0) {
        const initialParagraphCount = segment.paragraphs.length;
        
        // Remove paragraphs that:
        // 1. Have no ID
        // 2. Have no elements
        // 3. Have only non-text elements
        segment.paragraphs = segment.paragraphs.filter(paragraph => {
          if (!paragraph.id) {
            stats.emptyParagraphsRemoved++;
            return false;
          }
          
          if (!paragraph.elements || paragraph.elements.length === 0) {
            stats.emptyParagraphsRemoved++;
            return false;
          }
          
          const hasTextElement = paragraph.elements.some(el => el.type === 'text');
          if (!hasTextElement) {
            stats.emptyParagraphsRemoved++;
            return false;
          }
          
          // Validate timing values
          if (paragraph.startTime === undefined || paragraph.endTime === undefined) {
            return false;
          }
          
          if (paragraph.startTime > paragraph.endTime) {
            paragraph.endTime = paragraph.startTime + 0.1; // Small adjustment to make timing valid
          }
          
          return true;
        });
        
        stats.emptyParagraphsRemoved += (initialParagraphCount - segment.paragraphs.length);
      }
    }
    
    // Now remove empty segments (those without paragraphs)
    const initialSegmentCount = this.container.segments.length;
    this.container.segments = this.container.segments.filter(segment => {
      if (!segment.paragraphs || segment.paragraphs.length === 0) {
        return false;
      }
      return true;
    });
    
    stats.emptySegmentsRemoved = initialSegmentCount - this.container.segments.length;
    
    // Sort segments by time to ensure proper ordering for the merging step
    this.sortSegmentsByTime();
    
    // Merge adjacent segments with the same speaker if requested
    if (performMerge && this.container.segments.length > 1) {
      // We need to work with a copy as we'll be modifying the array
      const segments = [...this.container.segments];
      const finalSegments = [];
      let currentMergeGroup = [segments[0]];
      
      for (let i = 1; i < segments.length; i++) {
        const currentSegment = segments[i];
        const previousSegment = currentMergeGroup[currentMergeGroup.length - 1];
        
        // Check if this segment can be merged with the previous one
        if (
          previousSegment.speakerLabel === currentSegment.speakerLabel && 
          // Optional: add timing adjacency check
          previousSegment.endTime >= currentSegment.startTime - 1 // Allow up to 1s gap
        ) {
          // Add to the current merge group
          currentMergeGroup.push(currentSegment);
        } else {
          // Cannot merge with previous, process the current merge group
          if (currentMergeGroup.length > 1) {
            // Merge these segments
            const mergedSegment = this.mergeSegments(currentMergeGroup);
            finalSegments.push(mergedSegment);
            
            // Track merge statistics
            stats.segmentsMerged += (currentMergeGroup.length - 1);
            stats.elementsReassigned += this.countElements(currentMergeGroup);
            
            // Notify observers about the merge
            this.notifyObservers(TranscriptEditorChangeType.SEGMENTS_MERGED, {
              mergedSegmentId: mergedSegment.id,
              originalSegmentIds: currentMergeGroup.map(s => s.id),
              paragraphCount: mergedSegment.paragraphs.length,
              elementsReassigned: stats.elementsReassigned
            });
          } else {
            // Just add the single segment
            finalSegments.push(currentMergeGroup[0]);
          }
          
          // Start a new merge group with the current segment
          currentMergeGroup = [currentSegment];
        }
      }
      
      // Process the last merge group
      if (currentMergeGroup.length > 1) {
        const mergedSegment = this.mergeSegments(currentMergeGroup);
        finalSegments.push(mergedSegment);
        
        // Track merge statistics
        stats.segmentsMerged += (currentMergeGroup.length - 1);
        stats.elementsReassigned += this.countElements(currentMergeGroup);
        
        // Notify observers about the merge
        this.notifyObservers(TranscriptEditorChangeType.SEGMENTS_MERGED, {
          mergedSegmentId: mergedSegment.id,
          originalSegmentIds: currentMergeGroup.map(s => s.id),
          paragraphCount: mergedSegment.paragraphs.length,
          elementsReassigned: stats.elementsReassigned
        });
      } else if (currentMergeGroup.length === 1) {
        finalSegments.push(currentMergeGroup[0]);
      }
      
      // Replace the container segments with our optimized list
      this.container.segments = finalSegments;
    }
    
    // Final sort after all modifications
    this.sortSegmentsByTime();
    
    // Update all references to ensure consistency
    this.updateAllReferences();
    
    return { valid: true, stats };
  }
  
  /**
   * Counts the total number of elements in an array of segments
   * Used for statistics about how many elements were reassigned during segment merging
   */
  private countElements(segments: any[]): number {
    let count = 0;
    for (const segment of segments) {
      for (const paragraph of segment.paragraphs) {
        count += paragraph.elements.length;
      }
    }
    return count;
  }
  
  private buildSegment(SegmentCls: any, speaker: string, paragraphs: Paragraph[]) {
    // Filter out empty paragraphs or paragraphs with only punctuation
    const validParagraphs = paragraphs.filter((para: Paragraph) => {
      const hasTextElements = para.elements.some((el) => el.type === 'text');
      const hasValidTiming = para.startTime !== undefined && para.endTime !== undefined;
      return hasTextElements && hasValidTiming;
    });
    
    if (validParagraphs.length === 0) {
      return null; // Return null for empty segments to be filtered out later
    }
    
    // Calculate segment start/end time from paragraphs
    const startTime = Math.min(...validParagraphs.map((p: Paragraph) => p.startTime || Infinity));
    const endTime = Math.max(...validParagraphs.map((p: Paragraph) => p.endTime || 0));
    
    // Create segment with pre-calculated timing to ensure correct ordering
    const seg = new SegmentCls(this.uid('seg1'), speaker, startTime, endTime, validParagraphs, []);
    
    seg.updateInternalData();
    // Double-check timing with editor method
    this.updateSegmentTiming(seg);
    return seg;
  }


  private uid(prefix: string) {
    return typeof crypto !== 'undefined' && (crypto as any).randomUUID
      ? `${prefix}_${crypto.randomUUID()}`
      : `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }

  /**
   * Merges multiple segments into a single segment
   * Preserves all paragraphs and their elements
   * @param segments Array of segments to merge (assumed to have the same speaker)
   * @returns The merged segment
   */
  private mergeSegments(segments: any[]): any {
    if (segments.length === 0) return null;
    if (segments.length === 1) return segments[0];
    
    const SegmentClass = this.getSegmentClass();
    
    const baseSegment: Segment = this.buildSegment(SegmentClass, segments[0].speakerLabel, segments[0].paragraphs);

    // Use the first segment as the base
    // const baseSegment: Segment = {...segments[0]} as Segment;
    const mergedParagraphs = [...baseSegment.paragraphs];
    
    // Calculate timing for the merged segment
    let startTime = baseSegment.startTime;
    let endTime = baseSegment.endTime;
    
    // Add paragraphs from the other segments
    for (let i = 1; i < segments.length; i++) {
      const segment = segments[i];
      
      // Update timing if needed
      startTime = Math.min(startTime, segment.startTime);
      endTime = Math.max(endTime, segment.endTime);
      
      // Update segmentId for all paragraphs and their elements
      for (const paragraph of segment.paragraphs) {
        paragraph.segmentId = baseSegment.id;
        
        // Update segmentId for all elements
        for (const element of paragraph.elements) {
          element.segmentId = baseSegment.id;
        }
        
        // Add to merged paragraphs
        mergedParagraphs.push(paragraph);
      }
    }
    
    // Update the base segment with the merged data
    baseSegment.startTime = startTime;
    baseSegment.endTime = endTime;
    baseSegment.paragraphs = mergedParagraphs;
    
    // Sort paragraphs by time
    this.sortParagraphsByTime(baseSegment);
    
    console.log('baseSegment before updateInternalData', baseSegment, segments[0]);
    // Update internal data
    baseSegment.updateInternalData();
    
    return baseSegment;
  }
  
  /**
   * Validates the container structure before saving
   * @returns Validation result with success flag and reason if invalid
   */
  private validateContainer(): { valid: boolean; reason?: string } {
    const validationResult = this.validateAndOptimizeContainer(false);
    return {
      valid: validationResult.valid,
      reason: validationResult.reason
    };
  }
  
  /**
   * Save manifest to S3
   * Stores the manifest directly as a container manifest
   * 
   * @param transcription Transcription to save
   */
  private async saveTranscription(transcription: TranscriptData): Promise<void> {
    try {
      // Use the new container-manifest endpoint
      const url = getApiUrl(`/api/story/${this.storyId}/transcript-data?resource=transcription&language=${this.language}&storyId=${this.storyId}`);
      
      
      // Additional validation before saving
      if (!transcription) {
        throw new Error('Cannot save null transcription');
      }
      
      // Import the compression utility dynamically to avoid circular dependencies
      const { compressTranscriptData } = await import('../utility/transcriptCompression');
      
      // Compress the transcription data
      const compressedResult = compressTranscriptData(transcription);
      
      const originalSize = JSON.stringify(transcription).length;
      const compressedSize = compressedResult.data.length;
      
      console.log(
        `Transcript compression statistics:
        - Original size: ${originalSize} bytes
        - Compressed size: ${compressedSize} bytes
        - Compression ratio: ${(compressedSize / originalSize * 100).toFixed(2)}%`
      );
      
      // The API expects the data to be wrapped in a 'data' field
      // For compressed data, we use a special format to indicate it's compressed
      const transcriptionRequestBody = {
        data: compressedResult
      };
      
      const transcriptionResponse = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Content-Compressed': 'true' // Add a header to indicate compression
        },
        body: JSON.stringify(transcriptionRequestBody)
      });
      
      // Check for HTTP errors and provide detailed error messages
      if (!transcriptionResponse.ok) {
        let errorText = '';
        try {
          errorText = await transcriptionResponse.text();
        } catch {
          errorText = 'Could not read error response';
        }
        
        // Log detailed error information
        console.error('Failed to save transcription:', {
          status: transcriptionResponse.status,
          statusText: transcriptionResponse.statusText,
          url,
          errorText
        });
        
        // Provide specific error message based on status code
        if (transcriptionResponse.status === 400) {
          throw new Error(`Failed to save transcription: Bad Request - Server rejected the transcription data: ${errorText}`);
        } else if (transcriptionResponse.status === 401 || transcriptionResponse.status === 403) {
          throw new Error(`Failed to save transcription: Authentication/Authorization error (${transcriptionResponse.status}): ${errorText}`);
        } else if (transcriptionResponse.status === 404) {
          throw new Error(`Failed to save manifest: Endpoint not found - Check if the story ID or API path is correct: ${errorText}`);
        } else if (transcriptionResponse.status === 500) {
          throw new Error(`Failed to save transcription: Server error - The server encountered an internal error: ${errorText}`);
        } else {
          throw new Error(`Failed to save transcription: ${transcriptionResponse.statusText} (${transcriptionResponse.status}) - ${errorText}`);
        }
      }     
      
    } catch (error) {
      console.error('Error in saveTranscription:', error);
      throw error;
    }
  }
  

  /**
   * Discard all pending changes
   * 
   * @returns True if changes were discarded successfully
   */
  async discardChanges(): Promise<boolean> {
    try {
      if (!this.container) {
        return true;
      }
      
      // Clear pending operations
      this.pendingOperations = [];
      
      // Notify observers that changes were discarded
      this.notifyObservers(TranscriptEditorChangeType.CHANGES_DISCARDED, {
        container: this.container
      });
      
      return true;
    } catch (error) {
      console.error('Error discarding changes:', error);
      
      // Notify observers of error
      this.notifyObservers(TranscriptEditorChangeType.ERROR_OCCURRED, {
        error: error instanceof Error ? error.message : 'Unknown error during discard'
      });
      
      return false;
    }
  }
  
  /**
   * Get the container
   */
  getContainer(): TranscriptContainer | null {
    return this.container;
  }
  
  /**
   * Updates all parent-child references throughout the transcript
   * Call this after operations that significantly modify the transcript structure
   */
  updateAllReferences(): void {
    const container = this.getContainer();
    if (!container) return;
    
    // Update references for all segments and their children
    container.segments.forEach(segment => {
      segment.updateInternalData();
    });

    container.rebuildSegmentMap();
    
    // Notify observers that references have been updated
    this.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_CHANGED, {
      container: container
    });
  }
  
  /**
   * Get all segments in the container
   */
  getAllSegments(): Segment[] {
    return this.container ? this.container.segments : [];
  }
  
  /**
   * Get a segment by ID
   * 
   * @param segmentId ID of the segment to get
   */
  getSegment(segmentId: string): Segment | undefined {
    return this.container ? this.container.getSegment(segmentId) : undefined;
  }
  
  /**
   * Get a paragraph by ID
   * 
   * @param segmentId ID of the segment containing the paragraph
   * @param paragraphId ID of the paragraph to get
   */
  getParagraph(segmentId: string, paragraphId: string): Paragraph | undefined {
    const segment = this.getSegment(segmentId);
    return segment ? segment.getParagraph(paragraphId) : undefined;
  }
  
  /**
   * Get segments that overlap with a given time range
   * 
   * @param startTime Start time of the range in seconds
   * @param endTime End time of the range in seconds
   */
  findSegmentsByTime(startTime: number, endTime: number): Segment[] {
    if (!this.container) return [];
    
    return this.container.segments.filter(segment => 
      segment.startTime <= endTime && segment.endTime >= startTime
    );
  }
  
  /**
   * Sort segments by start time to ensure proper ordering
   * This should be called after operations that create or modify segments
   */
  sortSegmentsByTime(): void {
    if (!this.container) return;
    // Debug info before sorting
    const beforeSorting = this.container.segments.map(s => ({
      id: s.id,
      speakerLabel: s.speakerLabel,
      startTime: s.startTime,
      endTime: s.endTime
    }));
    
    // Sort segments by start time
    this.container.segments.sort((a, b) => {
      // First check start time
      if (a.startTime !== b.startTime) {
        return a.startTime - b.startTime;
      }
      
      // If start times are the same, sort by end time
      return a.endTime - b.endTime;
    });
    
    // Debug info after sorting
    const afterSorting = this.container.segments.map(s => ({
      id: s.id,
      speakerLabel: s.speakerLabel,
      startTime: s.startTime,
      endTime: s.endTime
    }));
    
    // Also sort paragraphs within each segment by time
    for (const segment of this.container.segments) {
      this.sortParagraphsByTime(segment);
    }
    
    
    // Mark that changes have occurred
    this.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_CHANGED, {
      container: this.container
    });
  }
  
  /**
   * Sort paragraphs within a segment by start time
   * This ensures proper chronological ordering of paragraphs
   * 
   * @param segment The segment containing paragraphs to sort
   */
  sortParagraphsByTime(segment: Segment): void {
    if (!segment || !segment.paragraphs || segment.paragraphs.length <= 1) return;

    // Debug info before sorting
    const beforeSorting = segment.paragraphs.map(p => ({
      id: p.id,
      startTime: p.startTime,
      endTime: p.endTime
    }));
    
    // Sort paragraphs by start time
    segment.paragraphs.sort((a, b) => {
      // First check start time
      if (a.startTime !== b.startTime) {
        return a.startTime - b.startTime;
      }
      
      // If start times are the same, sort by end time
      return a.endTime - b.endTime;
    });

    // Increment the counter for each paragraph
    for (const paragraph of segment.paragraphs) {
      paragraph.incrementCounter();
    }

    // Increment the counter for the segment
    segment.incrementCounter();
    
  }
  
  /**
   * Get paragraphs that overlap with a given time range
   * 
   * @param startTime Start time of the range in seconds
   * @param endTime End time of the range in seconds
   */
  findParagraphsByTime(startTime: number, endTime: number): Array<{
    segment: Segment;
    paragraph: Paragraph;
  }> {
    const results: Array<{ segment: Segment; paragraph: Paragraph }> = [];
    
    if (!this.container) return results;
    
    // Find segments that overlap with the time range
    const segments = this.findSegmentsByTime(startTime, endTime);
    
    // For each segment, find paragraphs that overlap with the time range
    for (const segment of segments) {
      const paragraphs = segment.getParagraphsByTimeRange(startTime, endTime);
      
      for (const paragraph of paragraphs) {
        results.push({ segment, paragraph });
      }
    }
    
    return results;
  }
  
  /**
   * Update text content of an element with debouncing
   * 
   * This method schedules text updates to be processed in batches,
   * improving performance when users are actively typing
   * 
   * @param segmentId ID of the segment containing the element
   * @param paragraphId ID of the paragraph containing the element
   * @param elementId ID of the element to update
   * @param newText New text content
   * @param userId ID of the user making the change
   * @param immediate Whether to process the update immediately (for final updates)
   * @returns True if the element update was scheduled successfully
   */
  updateElementTextDebounced(
    segmentId: string,
    paragraphId: string,
    elementId: string,
    newText: string,
    userId: string,
    immediate: boolean = false
  ): boolean {
    if (!this.container) return false;
    
    // Unique key for this element
    const editKey = `${segmentId}:${paragraphId}:${elementId}`;
    
    // Create or update pending edit
    this.pendingTextEdits.set(editKey, {
      segmentId,
      paragraphId,
      elementId,
      text: newText,
      userId,
      timestamp: Date.now()
    });
    
    // If immediate update requested, process now
    if (immediate) {
      this.processPendingTextEdits();
      return true;
    }
    
    // Clear existing timeout if there is one
    if (this.textEditTimeout !== null) {
      window.clearTimeout(this.textEditTimeout);
    }
    
    // Schedule processing after delay (e.g., 500ms)
    this.textEditTimeout = window.setTimeout(() => {
      this.processPendingTextEdits();
    }, 500);
    
    return true;
  }
  
  /**
   * Process all pending text edits as a batch
   */
  private processPendingTextEdits(): void {
    if (this.pendingTextEdits.size === 0) return;
    
    
    // Begin batch operation to improve performance
    this.beginBatch();
    
    try {
      // Process each pending edit
      for (const edit of this.pendingTextEdits.values()) {
        // Check if the text is empty, which means we should delete the element
        if (edit.text.trim() === '') {
          this.deleteElement(edit.segmentId, edit.paragraphId, edit.elementId, edit.userId);
          continue;
        }
        
        // Apply the edit directly using the non-debounced method
        this.updateElementText(
          edit.segmentId,
          edit.paragraphId,
          edit.elementId,
          edit.text,
          edit.userId
        );
      }
    } finally {
      // Clear pending edits
      this.pendingTextEdits.clear();
      
      // Reset timeout
      this.textEditTimeout = null;
      
      // End batch operation
      this.endBatch();
    }
    
    // Check for untimed text elements that need to be processed
    this.triggerUntimedTextProcessing();
  }
  
  /**
   * Trigger processing of untimed text elements
   * Public method to allow external components to initiate alignment
   */
  triggerUntimedTextProcessing(): void {
    if (this.untimedTextElements.length === 0) return;
    
    // Call the async implementation
    this.processUntimedTextElements().catch(error => {
      console.error('Error processing untimed text elements:', error);
    });
  }
  
  /**
   * Add new text without timing data
   * This method collects context around the untimed text for forced alignment
   * 
   * @param segmentId ID of the segment
   * @param paragraphId ID of the paragraph
   * @param elementId ID of the element (if adding to existing element)
   * @param text The text to add
   * @param position Where the text is being added: 'start', 'end', or 'middle'
   */
  addUntimedText(
    segmentId: string,
    paragraphId: string,
    elementId: string,
    text: string,
    position: 'start' | 'end' | 'middle'
  ): void {
    if (!this.container) {
      console.error('No container available, cannot add untimed text');
      return;
    }
    
    const segment = this.container.getSegment(segmentId);
    if (!segment) {
      console.error(`Segment ${segmentId} not found, cannot add untimed text`);
      return;
    }
    
    const paragraph = segment.getParagraph(paragraphId);
    if (!paragraph) {
      console.error(`Paragraph ${paragraphId} not found in segment ${segmentId}, cannot add untimed text`);
      return;
    }
    
    const element = paragraph.getElement(elementId);
    if (!element || element.type !== 'text') {
      console.error(`Element ${elementId} not found or is not a text element, cannot add untimed text`);
      return;
    }
    
    // Get timing information for the element
    const elementStartTime = (element as TextElement).startTime || 0;
    const elementEndTime = (element as TextElement).endTime || 0;
    
    // Default assumption for timing boundaries
    let originalStart = elementStartTime;
    let originalEnd = elementEndTime;
    
    // Initial alignment boundaries with 3s buffer on each side
    let alignmentStart = Math.max(0, originalStart - 3);
    let alignmentEnd = originalEnd + 3;
    
    // Arrays to hold context words before and after
    const contextWordsBefore: Array<{
      text: string;
      startTime: number;
      endTime: number;
      elementId: string;
    }> = [];
    
    const contextWordsAfter: Array<{
      text: string;
      startTime: number;
      endTime: number;
      elementId: string;
    }> = [];
    
    // Find all text elements in the paragraph and neighboring paragraphs
    // to collect context for alignment
    
    // Find all paragraphs in the transcript that might provide context
    const allParagraphs = this.findParagraphsForContext(segment, paragraph, position);
    
    // Process paragraphs to gather context words
    this.collectContextWords(
      allParagraphs, 
      paragraph.id, 
      elementId, 
      position,
      contextWordsBefore,
      contextWordsAfter
    );
    
    // Refine alignment boundaries based on context
    if (contextWordsBefore.length > 0) {
      alignmentStart = Math.max(0, contextWordsBefore[0].startTime - 3);
      originalStart = contextWordsBefore[contextWordsBefore.length - 1].endTime;
    }
    
    if (contextWordsAfter.length > 0) {
      alignmentEnd = contextWordsAfter[contextWordsAfter.length - 1].endTime + 3;
      originalEnd = contextWordsAfter[0].startTime;
    }
    
    // Ensure we don't end up with invalid timing
    if (originalEnd < originalStart) {
      originalEnd = originalStart + 0.1; // Small buffer if timing is wrong
    }
    
    // Create untimed text entry with appropriate context for forced alignment
    const untimedTextEntry: UntimingText = {
      segmentId,
      paragraphId,
      elementId,
      text,
      position,
      timeContext: {
        alignmentStart,
        alignmentEnd,
        originalStart,
        originalEnd,
        contextWordsBefore,
        contextWordsAfter
      }
    };
    
    // Add to the untimed text list for later processing
    this.untimedTextElements.push(untimedTextEntry);
    
    
    // Notify that we have untimed text that needs attention
    this.notifyObservers(TranscriptEditorChangeType.UNTIMED_TEXT_ADDED, {
      segmentId,
      paragraphId,
      elementId,
      text,
      position,
      count: this.untimedTextElements.length,
      timeContext: untimedTextEntry.timeContext
    });
  }
  
  /**
   * Find paragraphs that can provide context for untimed text
   * 
   * @param segment The segment containing the paragraph with untimed text
   * @param paragraph The paragraph containing the untimed text
   * @param position The position of untimed text (start, end, middle)
   * @returns Array of paragraph objects with their segments that provide context
   */
  private findParagraphsForContext(
    segment: Segment,
    paragraph: Paragraph,
    position: 'start' | 'end' | 'middle'
  ): Array<{ segment: Segment; paragraph: Paragraph }> {
    if (!this.container) return [];
    
    const result: Array<{ segment: Segment; paragraph: Paragraph }> = [];
    
    // Add the current paragraph
    result.push({ segment, paragraph });
    
    // Find paragraphs before or after based on position
    const paragraphIndex = segment.paragraphs.findIndex(p => p.id === paragraph.id);
    
    if (position === 'start' || position === 'middle') {
      // Need context from paragraphs before
      if (paragraphIndex > 0) {
        // Add the previous paragraph in this segment
        result.push({ segment, paragraph: segment.paragraphs[paragraphIndex - 1] });
      } else {
        // Need to look in the previous segment
        const segmentIndex = this.container.segments.findIndex(s => s.id === segment.id);
        if (segmentIndex > 0) {
          const prevSegment = this.container.segments[segmentIndex - 1];
          if (prevSegment.paragraphs.length > 0) {
            // Add the last paragraph of the previous segment
            result.push({ 
              segment: prevSegment, 
              paragraph: prevSegment.paragraphs[prevSegment.paragraphs.length - 1] 
            });
          }
        }
      }
    }
    
    if (position === 'end' || position === 'middle') {
      // Need context from paragraphs after
      if (paragraphIndex < segment.paragraphs.length - 1) {
        // Add the next paragraph in this segment
        result.push({ segment, paragraph: segment.paragraphs[paragraphIndex + 1] });
      } else {
        // Need to look in the next segment
        const segmentIndex = this.container.segments.findIndex(s => s.id === segment.id);
        if (segmentIndex < this.container.segments.length - 1) {
          const nextSegment = this.container.segments[segmentIndex + 1];
          if (nextSegment.paragraphs.length > 0) {
            // Add the first paragraph of the next segment
            result.push({ 
              segment: nextSegment, 
              paragraph: nextSegment.paragraphs[0] 
            });
          }
        }
      }
    }
    
    return result;
  }
  
  /**
   * Collect context words from paragraphs for alignment
   * 
   * @param paragraphs Paragraphs to extract context from
   * @param targetParagraphId ID of the paragraph containing untimed text
   * @param targetElementId ID of the element containing untimed text
   * @param position Position of untimed text (start, end, middle)
   * @param contextWordsBefore Array to populate with words before untimed text
   * @param contextWordsAfter Array to populate with words after untimed text
   */
  private collectContextWords(
    paragraphs: Array<{ segment: Segment; paragraph: Paragraph }>,
    targetParagraphId: string,
    targetElementId: string,
    position: 'start' | 'end' | 'middle',
    contextWordsBefore: Array<{ text: string; startTime: number; endTime: number; elementId: string }>,
    contextWordsAfter: Array<{ text: string; startTime: number; endTime: number; elementId: string }>
  ): void {
    // Track whether we've reached the target element
    let foundTargetElement = false;
    const MAX_CONTEXT_WORDS = 20; // Limit words for alignment context
    
    // For each paragraph, collect text elements
    for (const { paragraph } of paragraphs) {
      // Get all text elements in the paragraph
      const textElements = paragraph.elements
        .filter(el => el.type === 'text')
        .map(el => ({
          element: el as TextElement,
          inTargetParagraph: paragraph.id === targetParagraphId
        }));
      
      for (const { element, inTargetParagraph } of textElements) {
        const isTargetElement = inTargetParagraph && element.id === targetElementId;
        
        // Determine if this element should be collected as context
        if (isTargetElement) {
          foundTargetElement = true;
          // Skip the target element itself, as it contains untimed text
          continue;
        }
        
        // For elements with timing data, use them as context
        if (element.startTime !== undefined && element.endTime !== undefined) {
          // Split the element text into words
          const words = element.value.trim().split(/\s+/);
          
          // Create word-level timing (approximate by dividing element time)
          const wordDuration = (element.endTime - element.startTime) / Math.max(1, words.length);
          
          // Create individual timed words
          const timedWords = words.map((word, index) => {
            const wordStartTime = element.startTime! + index * wordDuration;
            const wordEndTime = wordStartTime + wordDuration;
            
            return {
              text: word,
              startTime: wordStartTime,
              endTime: wordEndTime,
              elementId: element.id
            };
          });
          
          // Add words to appropriate context
          if (!foundTargetElement) {
            // This element is before the target
            if (contextWordsBefore.length < MAX_CONTEXT_WORDS) {
              // If we're at start/middle position, we want words BEFORE
              // If we're at end position, we DON'T want words before (they're after the target)
              if (position !== 'end') {
                contextWordsBefore.push(...timedWords);
                if (contextWordsBefore.length > MAX_CONTEXT_WORDS) {
                  // Keep only the most recent MAX_CONTEXT_WORDS
                  contextWordsBefore.splice(0, contextWordsBefore.length - MAX_CONTEXT_WORDS);
                }
              }
            }
          } else {
            // This element is after the target
            if (contextWordsAfter.length < MAX_CONTEXT_WORDS) {
              // If we're at end/middle position, we want words AFTER
              // If we're at start position, we DON'T want words after (they're before the target)
              if (position !== 'start') {
                contextWordsAfter.push(...timedWords);
                if (contextWordsAfter.length > MAX_CONTEXT_WORDS) {
                  // Keep only the first MAX_CONTEXT_WORDS
                  contextWordsAfter.splice(MAX_CONTEXT_WORDS);
                }
              }
            }
          }
        }
      }
    }
  }
  
  /**
   * Process untimed text elements using the align-stack service
   * This prepares data for forced alignment and sends to alignment API
   */
  async processUntimedTextElements(): Promise<void> {
    if (this.untimedTextElements.length === 0) return;
    
    
    // Create a debug record for this alignment session
    const alignmentDebugData = {
      session: {
        timestamp: new Date().toISOString(),
        elementCount: this.untimedTextElements.length
      },
      configuration: {} as any,
      elements: [] as any[],
      results: [] as any[],
      summary: {
        processed: 0,
        failed: 0,
        alignmentSuccess: 0
      }
    };
    
    if (!this.audioUrl) {
      console.error('[ALIGNMENT DEBUG] No audio URL available in resources, cannot process untimed text');
      alignmentDebugData.configuration = { error: 'No audio URL available' };
      
      // Store debug information for inspection
      window._arborAlignmentDebug = alignmentDebugData;
      return;
    }
    
    // Get alignment service configuration from environment
    const alignApiUrl = process.env.REACT_APP_ALIGN_URL || 'https://align.arborplatform.ai/align';
    const alignApiKey = process.env.REACT_APP_ALIGN_KEY;
    
    if (!alignApiKey) {
      console.error('[ALIGNMENT DEBUG] No alignment API key available in environment (REACT_APP_ALIGN_KEY)');
      alignmentDebugData.configuration = { 
        error: 'No API key available',
        url: alignApiUrl
      };
      
      // Store debug information for inspection
      window._arborAlignmentDebug = alignmentDebugData;
      return;
    }
    
    // Record configuration
    alignmentDebugData.configuration = {
      alignApiUrl,
      hasApiKey: !!alignApiKey,
      audioUrl: this.audioUrl,
      engine: "gentle"
    };
    
    // Process each untimed text element
    const processedElements: string[] = [];
    const failedElements: string[] = [];
    
    for (const untimedElement of this.untimedTextElements) {
      try {
        // Create debug record for this element
        const elementDebug: {
          id: string;
          text: string;
          position: 'start' | 'end' | 'middle';
          timeContext: {
            alignmentStart: number;
            alignmentEnd: number;
            contextWordsBefore: number;
            contextWordsAfter: number;
          };
          request: any;
          response: any;
          error: any;
        } = {
          id: untimedElement.elementId,
          text: untimedElement.text,
          position: untimedElement.position,
          timeContext: {
            alignmentStart: untimedElement.timeContext.alignmentStart,
            alignmentEnd: untimedElement.timeContext.alignmentEnd,
            contextWordsBefore: untimedElement.timeContext.contextWordsBefore.length,
            contextWordsAfter: untimedElement.timeContext.contextWordsAfter.length
          },
          request: null as any,
          response: null as any,
          error: null as any
        };
        
        // Prepare the context for alignment
        const fullContext = this.prepareAlignmentContext(untimedElement);
        
        // Record the request in debug
        const requestBody = {
          engine: "gentle", // Using Gentle as it's more forgiving for OOV words
          s3_url: this.audioUrl,
          start: untimedElement.timeContext.alignmentStart,
          end: untimedElement.timeContext.alignmentEnd,
          text: fullContext.text
        };
        
        elementDebug.request = requestBody as any;
        
        // Call the alignment service
        const response = await fetch(alignApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Api-Key': alignApiKey
          },
          body: JSON.stringify(requestBody)
        });
        
        if (response.ok) {
          const alignmentResult: import('./types').AlignmentServiceResponse = await response.json();
          
          // Record response in debug
          elementDebug.response = alignmentResult;
          
          console.log('[ALIGNMENT DEBUG] Received alignment result:', {
            wordCount: alignmentResult.words.length,
            successCount: alignmentResult.words.filter(w => w.case === 'success').length,
            engine: alignmentResult.engine,
            clipDuration: alignmentResult.clip_duration,
            words: alignmentResult.words.map(w => ({
              word: w.word, 
              start: w.start.toFixed(3), 
              end: w.end.toFixed(3), 
              case: w.case
            }))
          });
          
          // Process the aligned words and create elements with timing
          await this.processAlignmentResult(untimedElement, alignmentResult, fullContext);
          
          // Track successful processing
          processedElements.push(untimedElement.elementId);
          alignmentDebugData.summary.alignmentSuccess++;
        } else {
          // Handle error response
          let errorDetail = 'Unknown error';
          let errorData = null;
          
          try {
            errorData = await response.json();
            errorDetail = errorData.detail || errorData.message || 'Unknown error';
          } catch (e) {
            errorDetail = `HTTP ${response.status}: ${response.statusText}`;
          }
          
          // Record error in debug
          elementDebug.error = {
            status: response.status,
            statusText: response.statusText,
            detail: errorDetail,
            data: errorData
          } as any;
          
          console.error(`[ALIGNMENT DEBUG] Alignment service error: ${errorDetail}`);
          failedElements.push(untimedElement.elementId);
          alignmentDebugData.summary.failed++;
        }
        
        // Add element debug record to the main debug data
        alignmentDebugData.elements.push(elementDebug);
      } catch (error) {
        // Record unexpected error
        const errorDebug = {
          id: untimedElement.elementId,
          text: untimedElement.text,
          position: untimedElement.position,
          error: {
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined
          }
        };
        
        alignmentDebugData.elements.push(errorDebug);
        
        console.error(`[ALIGNMENT DEBUG] Error processing untimed text element:`, error);
        failedElements.push(untimedElement.elementId);
        alignmentDebugData.summary.failed++;
      }
    }
    
    // Update summary counts
    alignmentDebugData.summary.processed = processedElements.length;
    
    // Store debug information in a global variable for inspection
    window._arborAlignmentDebug = alignmentDebugData;
    
    
    // Remove successfully processed items
    this.untimedTextElements = this.untimedTextElements.filter(
      element => !processedElements.includes(element.elementId)
    );
    
    // Notify about processing completion
    this.notifyObservers(TranscriptEditorChangeType.UNTIMED_TEXT_PROCESSED, {
      processedCount: processedElements.length,
      failedCount: failedElements.length,
      remainingCount: this.untimedTextElements.length,
      debugInfo: {
        availableAt: 'window._arborAlignmentDebug',
        timestamp: alignmentDebugData.session.timestamp
      }
    });
  }
  
  /**
   * Prepare the full context text for alignment
   * Combines the context words with the untimed text
   */
  private prepareAlignmentContext(untimedElement: UntimingText): {
    text: string;
    beforeWordCount: number;
    afterWordCount: number;
  } {
    // Extract words before and after
    const beforeWords = untimedElement.timeContext.contextWordsBefore.map(w => w.text);
    const afterWords = untimedElement.timeContext.contextWordsAfter.map(w => w.text);
    
    // Split the untimed text into words
    const untimedWords = untimedElement.text.trim().split(/\s+/);
    
    // Combine all words for the full context
    let fullContextText: string;
    
    // Arrange the words based on the position
    switch (untimedElement.position) {
      case 'start':
        // Untimed words come before the after-context
        fullContextText = [...untimedWords, ...afterWords].join(' ');
        break;
        
      case 'end':
        // Untimed words come after the before-context
        fullContextText = [...beforeWords, ...untimedWords].join(' ');
        break;
        
      case 'middle':
      default:
        // Untimed words are between before and after context
        fullContextText = [...beforeWords, ...untimedWords, ...afterWords].join(' ');
        break;
    }
    
    return {
      text: fullContextText,
      beforeWordCount: beforeWords.length,
      afterWordCount: afterWords.length
    };
  }
  
  /**
   * Process the alignment result and create new text elements with timing
   */
  private async processAlignmentResult(
    untimedElement: import('./types').UntimingText,
    alignmentResult: import('./types').AlignmentServiceResponse,
    context: { text: string; beforeWordCount: number; afterWordCount: number }
  ): Promise<void> {
    // Create a detailed debug record for this processing
    const processingDebug: {
      element: {
        id: string;
        position: 'start' | 'end' | 'middle';
        text: string;
      };
      context: {
        beforeWordCount: number;
        afterWordCount: number;
        fullText: string;
      };
      result: {
        totalWords: number;
        successWords: number;
      };
      processing: {
        foundSegment: boolean;
        foundParagraph: boolean;
        foundElement: boolean;
        relevantWords: number;
        startTime: number | null;
        endTime: number | null;
        combinedText: string | null;
        oldText: string | null;
        newText: string | null;
        success: boolean;
        error: any;
      }
    } = {
      element: {
        id: untimedElement.elementId,
        position: untimedElement.position,
        text: untimedElement.text,
      },
      context: {
        beforeWordCount: context.beforeWordCount,
        afterWordCount: context.afterWordCount,
        fullText: context.text,
      },
      result: {
        totalWords: alignmentResult.words.length,
        successWords: alignmentResult.words.filter(w => w.case === 'success').length,
      },
      processing: {
        foundSegment: false,
        foundParagraph: false,
        foundElement: false,
        relevantWords: 0,
        startTime: null,
        endTime: null,
        combinedText: null,
        oldText: null,
        newText: null,
        success: false,
        error: null
      }
    };
    
    // Store this in the global debug data
    if (!window._arborAlignmentDebug.results) {
      window._arborAlignmentDebug.results = [];
    }
    window._arborAlignmentDebug.results.push(processingDebug);
    
    try {
      if (!this.container) {
        console.error('[ALIGNMENT DEBUG] No container available, cannot process alignment result');
        processingDebug.processing.error = 'No container available' as any;
        return;
      }
      
      // Find the segment and paragraph
      const segment = this.container.getSegment(untimedElement.segmentId);
      if (!segment) {
        console.error(`[ALIGNMENT DEBUG] Segment ${untimedElement.segmentId} not found, cannot process alignment result`);
        processingDebug.processing.error = `Segment ${untimedElement.segmentId} not found` as any;
        return;
      }
      processingDebug.processing.foundSegment = true;
      
      const paragraph = segment.getParagraph(untimedElement.paragraphId);
      if (!paragraph) {
        console.error(`[ALIGNMENT DEBUG] Paragraph ${untimedElement.paragraphId} not found, cannot process alignment result`);
        processingDebug.processing.error = `Paragraph ${untimedElement.paragraphId} not found` as any;
        return;
      }
      processingDebug.processing.foundParagraph = true;
      
      // Get the original element
      const originalElement = paragraph.getElement(untimedElement.elementId) as TextElement;
      if (!originalElement || originalElement.type !== 'text') {
        console.error(`[ALIGNMENT DEBUG] Element ${untimedElement.elementId} not found or is not a text element`);
        processingDebug.processing.error = `Element ${untimedElement.elementId} not found or is not a text element` as any;
        return;
      }
      processingDebug.processing.foundElement = true;
      
      // Find the aligned words for the untimed text
      // Skip context words based on their position
      const alignedWords = [...alignmentResult.words];
      
      console.log('[ALIGNMENT DEBUG] Processing alignment result:', {
        position: untimedElement.position,
        totalWords: alignedWords.length,
        words: alignedWords.map(w => w.word).join(' '),
        beforeWords: context.beforeWordCount,
        afterWords: context.afterWordCount,
        originalElementId: originalElement.id
      });
      
      // Extract only the aligned words for the untimed text (not the context)
      let relevantWords: import('./types').TimedWord[] = [];
      
      switch (untimedElement.position) {
        case 'start':
          // The untimed text is at the start, so the first N words are relevant
          // where N is the total minus the after-context words
          relevantWords = alignedWords.slice(
            0, 
            Math.max(0, alignedWords.length - context.afterWordCount)
          );
          break;
          
        case 'end':
          // The untimed text is at the end, so the last N words are relevant
          // where N is the total minus the before-context words
          relevantWords = alignedWords.slice(
            Math.min(context.beforeWordCount, alignedWords.length)
          );
          break;
          
        case 'middle':
          // The untimed text is in the middle, between before and after context
          relevantWords = alignedWords.slice(
            Math.min(context.beforeWordCount, alignedWords.length),
            Math.max(0, alignedWords.length - context.afterWordCount)
          );
          break;
      }
      
      console.log('[ALIGNMENT DEBUG] Extracted relevant aligned words:', {
        position: untimedElement.position,
        totalWords: alignedWords.length,
        relevantWords: relevantWords.length,
        relevantWordsList: relevantWords.map(w => `${w.word}(${w.start.toFixed(2)}-${w.end.toFixed(2)})`).join(' '),
        beforeWords: context.beforeWordCount,
        afterWords: context.afterWordCount
      });
      
      processingDebug.processing.relevantWords = relevantWords.length;
      
      // Ensure we have words to process
      if (relevantWords.length === 0) {
        console.error('[ALIGNMENT DEBUG] No relevant aligned words found');
        processingDebug.processing.error = 'No relevant aligned words found' as any;
        return;
      }
      
      // Word-by-word debug of relevant words
      console.log('[ALIGNMENT DEBUG] Word-by-word timing:');
      for (let i = 0; i < relevantWords.length; i++) {
        const word = relevantWords[i];
        console.log(`  Word ${i+1}/${relevantWords.length}: "${word.word}" at ${word.start.toFixed(3)}-${word.end.toFixed(3)} (${word.case})`);
      }
      
      // Determine start and end times for the new text
      const startTime = relevantWords[0].start;
      const endTime = relevantWords[relevantWords.length - 1].end;
      
      processingDebug.processing.startTime = startTime;
      processingDebug.processing.endTime = endTime;
      
      // Combine the aligned words into a single element
      const combinedText = relevantWords.map(word => word.word).join(' ');
      processingDebug.processing.combinedText = combinedText as any;
      
      // Start a batch operation
      this.beginBatch();
      
      try {
        // Find the current text of the element
        const oldText = originalElement.value;
        processingDebug.processing.oldText = oldText as any;
        
        // Create the new text based on position
        let newText: string;
        
        switch (untimedElement.position) {
          case 'start':
            // Add aligned text at the beginning
            newText = `${combinedText} ${oldText}`;
            break;
            
          case 'end':
            // Add aligned text at the end
            newText = `${oldText} ${combinedText}`;
            break;
            
          case 'middle':
            // For middle position, we'd need more context to know exactly where to insert
            // For simplicity, we'll replace the entire text with the aligned version
            newText = combinedText;
            break;
        }
        
        processingDebug.processing.newText = newText as any;
        
        // Record the original timing for debugging
        const originalStartTime = originalElement.startTime;
        const originalEndTime = originalElement.endTime;
        
        console.log('[ALIGNMENT DEBUG] Updating element with aligned text:', {
          oldText,
          newText,
          oldTiming: { start: originalStartTime, end: originalEndTime },
          newTiming: { start: startTime, end: endTime }
        });
        
        // Use our dedicated command for alignment results
        const command = new ProcessAlignmentResultCommand(
          this,
          untimedElement.segmentId,
          untimedElement.paragraphId,
          untimedElement.elementId,
          newText,
          startTime,
          endTime
        );
        
        // Execute the command
        processingDebug.processing.success = await this.commandManager.execute(command);
      } finally {
        // End the batch operation
        this.endBatch();
      }
    } catch (error) {
      // Record error in debug
      console.error('[ALIGNMENT DEBUG] Error in processAlignmentResult:', error);
      processingDebug.processing.error = (error instanceof Error ? 
        { message: error.message, stack: error.stack } : String(error)) as any;
    }
  }
  
  /**
   * Update text content of an element
   * 
   * @param segmentId ID of the segment containing the element
   * @param paragraphId ID of the paragraph containing the element
   * @param elementId ID of the element to update
   * @param newText New text content
   * @param userId ID of the user making the change
   * @returns True if the element was updated successfully
   */
  updateElementText(
    segmentId: string,
    paragraphId: string,
    elementId: string,
    newText: string,
    userId: string
  ): boolean {
    if (!this.container) return false;
    
    const segment = this.container.getSegment(segmentId);
    if (!segment) return false;
    
    const paragraph = segment.getParagraph(paragraphId);
    if (!paragraph) return false;
    
    const element = paragraph.getElement(elementId);
    if (!element || element.type !== 'text') return false;
    
    // Store old text for potential undo
    const oldText = element.value;
    
    // Check if the new text is empty, which means we should delete the element
    if (newText.trim() === '') {
      // Delegate to deleteElement method
      return this.deleteElement(segmentId, paragraphId, elementId, userId);
    }
    
    // Check if this is untimed text by comparing length with original
    // (Assuming adding new words means they don't have timing)
    const isAddingText = newText.length > oldText.length;
    const words = newText.trim().split(/\s+/);
    const oldWords = oldText.trim().split(/\s+/);
    
    // ************************************************************************
    // Detect text additions (from single word to multiple words)
    // This is the critical check for untimed text that triggers forced alignment
    // ************************************************************************
    if (isAddingText && words.length > oldWords.length) {
      // Determine position of new text based on diff analysis
      let position: 'start' | 'end' | 'middle' = 'end';
      let untimedText = '';
      
      if (newText.startsWith(oldText)) {
        // Text added at the end
        position = 'end';
        untimedText = newText.substring(oldText.length).trim();
      } else if (newText.endsWith(oldText)) {
        // Text added at the beginning
        position = 'start';
        untimedText = newText.substring(0, newText.length - oldText.length).trim();
      } else {
        // Text added in the middle or multiple places
        position = 'middle';
        
        // More sophisticated diff would be needed for precise middle text
        // For now, use the entire new text as we'll use context for alignment
        untimedText = newText;
      }
      
      // Track the untimed text with context for forced alignment
      this.addUntimedText(
        segmentId,
        paragraphId,
        elementId,
        untimedText,
        position
      );
    }
    
    // Create and execute the dedicated command
    const command = new UpdateElementTextCommand(
      this,
      segmentId,
      paragraphId,
      elementId,
      newText,
      userId
    );
    
    // Add command to command manager for undo/redo
    // Now we return the Promise from execute instead of a direct boolean
    this.commandManager.execute(command);
    return true;
  }
  
  /**
   * Update all elements in a paragraph
   * 
   * @param segmentId ID of the segment containing the paragraph
   * @param paragraphId ID of the paragraph to update
   * @param newElements New elements to replace existing ones
   * @param userId ID of the user making the change
   * @returns True if the paragraph was updated successfully
   */
  updateParagraphElements(
    segmentId: string,
    paragraphId: string,
    newElements: Array<import('./models/TranscriptElement').TranscriptElement>,
    userId: string
  ): boolean {
    if (!this.container) return false;
    
    const segment = this.container.getSegment(segmentId);
    if (!segment) return false;
    
    const paragraph = segment.getParagraph(paragraphId);
    if (!paragraph) return false;
    
    // Create and execute the dedicated command
    const command = new UpdateParagraphElementsCommand(
      this,
      segmentId,
      paragraphId,
      newElements,
      userId
    );
    
    // Add command to command manager for undo/redo
    // Now we return the Promise from execute instead of a direct boolean
    this.commandManager.execute(command);
    return true;
  }
  

  
  /**
   * Move a paragraph to another segment
   * 
   * @param sourceParagraph The paragraph to move
   * @param sourceSegmentId ID of the segment containing the paragraph
   * @param targetSegmentId ID of the segment to move the paragraph to
   * @param userId ID of the user making the change
   * @returns True if the paragraph was moved successfully
   */
  moveParagraphToSegment(
    sourceSegmentId: string,
    sourceParagraphId: string,
    targetSegmentId: string,
    userId: string
  ): boolean {
    if (!this.container) return false;
    
    // Validate that the segments and paragraph exist
    const sourceSegment = this.container.getSegment(sourceSegmentId);
    const targetSegment = this.container.getSegment(targetSegmentId);
    
    if (!sourceSegment || !targetSegment) {
      console.error(`Cannot move paragraph: one or both segments not found`);
      return false;
    }
    
    const sourceParagraph = sourceSegment.getParagraph(sourceParagraphId);
    if (!sourceParagraph) {
      console.error(`Cannot move paragraph: paragraph not found in source segment`);
      return false;
    }
    
    // Create and execute MoveParagraphToSegmentCommand
    const command = new MoveParagraphToSegmentCommand(
      this,
      sourceSegmentId,
      sourceParagraphId,
      targetSegmentId,
      userId
    );
    
    // Execute the command through the command manager
    this.commandManager.execute(command);
    return true;
  }
  
  /**
   * Record an operation to the pending operations list
   * 
   * @param operation Operation to record
   */
  recordOperation(operation: EditOperation): void {
    this.pendingOperations.push(operation);
  }
  
  /**
   * Get the current language
   */
  getCurrentLanguage(): string {
    return this.language;
  }

  /**
   * Get speaker mappings from SpeakerService
   */
  getSpeakerMappings(): TranscriptVoiceMapping | null {
    if (!this.speakerService) {
      console.warn('Cannot get speaker mappings: Speaker service not available');
      return null;
    }
    
    return this.speakerService.getSpeakerMappings();
  }
  
  /**
   * Get waveform data
   */
  getWaveformData(): WaveformData | null {
    return this.waveformData;
  }
  
  /**
   * Set the speaker service directly
   * 
   * @param speakerService The SpeakerService instance
   */
  setSpeakerService(speakerService: SpeakerService): void {
    this.speakerService = speakerService;
  }
  
  /**
   * Get the speaker service
   * 
   * @returns The speaker service instance or null
   */
  getSpeakerService(): SpeakerService | null {
    return this.speakerService;
  }
  
  /**
   * Set the selection service
   * 
   * @param service The SelectionService instance to set
   */
  setSelectionService(service: SelectionService): void {
    this.selectionService = service;
  }
  
  /**
   * Get the selection service
   * 
   * @returns The selection service instance or null
   */
  getSelectionService(): SelectionService | null {
    return this.selectionService;
  }
  
  /**
   * Set the timeline service
   * 
   * @param service The TimelineService instance to set
   */
  setTimelineService(service: TimelineService): void {
    this.timelineService = service;
  }
  
  /**
   * Get the timeline service
   * 
   * @returns The timeline service instance or null
   */
  getTimelineService(): TimelineService | null {
    return this.timelineService;
  }
  
  /**
   * Set the audio service
   * 
   * @param service The AudioService instance to set
   */
  setAudioService(service: AudioService): void {
    this.audioService = service;
  }
  
  /**
   * Get the audio service
   * 
   * @returns The audio service instance or null
   */
  getAudioService(): AudioService | null {
    return this.audioService;
  }
  

  nextSpeakerNumber(): number {
    const container = this.container;
    if (!container) return 0;
    const nums = container.segments
      .map((s: Segment) => /^spk_(\d+)$/.exec(s.speakerLabel)?.[1])
      .filter(Boolean)
      .map((n: string | undefined) => parseInt(n!, 10));
    return (nums.length ? Math.max(...nums) : -1) + 1;
  }
  

  /**
   * Update speaker mapping and persist it to storage using SpeakerService
   * 
   * @param speakerLabel Anonymous speaker label (e.g., "spk_0") 
   * @param datoSpeakerId ID of the speaker in DatoCMS
   */
  updateSpeakerMapping(speakerLabel: string, datoSpeakerId: string): void {
    if (!this.speakerService) {
      console.error('Cannot update speaker mapping: Speaker service not available');
      return;
    }
    
    // Verify that speakerLabel is an anonymous label (should start with spk_)
    if (!speakerLabel.startsWith('spk_')) {
      // Continue anyway since this might be a migration case
    }
    
    // Use the speaker service to update the mapping
    this.speakerService.updateSpeakerMapping(speakerLabel, datoSpeakerId);

    // Notify observers about the UI updates
    this.notifyObservers(TranscriptEditorChangeType.SPEAKER_ASSIGNED, {
      speakerLabel, 
      datoSpeakerId
    });
  }
  
  /**
   * Gets the Paragraph class for creating new paragraphs
   * This allows commands to create paragraphs without importing the class directly
   */
  getParagraphClass() {
    return Paragraph;
  }
  
  /**
   * Gets the Segment class for creating new segments
   * This allows commands to create segments without importing the class directly
   */
  getSegmentClass() {
    return Segment;
  }
  
  /**
   * Updates timing for a segment based on its paragraphs
   * 
   * @param segment The segment to update timing for
   */
  updateSegmentTiming(segment: Segment): void {
    if (!segment || !segment.paragraphs || segment.paragraphs.length === 0) {
      return;
    }
    
    let startTime = Infinity;
    let endTime = 0;
    
    // Timing is determined by the paragraphs it contains
    for (const paragraph of segment.paragraphs) {
      if (paragraph.startTime !== undefined && paragraph.startTime < startTime) {
        startTime = paragraph.startTime;
      }
      
      if (paragraph.endTime !== undefined && paragraph.endTime > endTime) {
        endTime = paragraph.endTime;
      }
    }
    
    // Handle edge case of no valid timing
    if (startTime === Infinity) {
      startTime = 0;
      endTime = 0;
    }
    
    // Set the new timing
    segment.startTime = startTime;
    segment.endTime = endTime;
  }
  
  /**
   * Removes any segments that are empty (have no paragraphs)
   */
  removeEmptySegments(): void {
    if (!this.container) return;
    
    const emptySegments = this.container.segments.filter(segment => 
      !segment.paragraphs || segment.paragraphs.length === 0
    );
    
    for (const segment of emptySegments) {
      this.container.removeSegment(segment.id);
    }
  }
  
  /**
   * Delete a single element from a paragraph
   * If the paragraph becomes empty, it will be deleted as well
   * 
   * @param segmentId ID of the segment containing the element
   * @param paragraphId ID of the paragraph containing the element
   * @param elementId ID of the element to delete
   * @param userId ID of the user making the change
   * @returns True if the element was deleted successfully
   */
  deleteElement(
    segmentId: string,
    paragraphId: string,
    elementId: string,
    userId: string
  ): boolean {
    if (!this.container) return false;
    
    const segment = this.container.getSegment(segmentId);
    if (!segment) return false;
    
    const paragraph = segment.getParagraph(paragraphId);
    if (!paragraph) return false;
    
    const element = paragraph.getElement(elementId);
    if (!element) return false;
    
    // Create and execute the dedicated command
    const command = new DeleteElementCommand(
      this,
      segmentId,
      paragraphId,
      elementId,
      userId
    );
    
    // Add command to command manager for undo/redo
    // Now we return the Promise from execute instead of a direct boolean
    this.commandManager.execute(command);
    return true;
  }
  
  /**
   * Delete a paragraph from a segment
   * If the segment becomes empty, it will be deleted as well
   * 
   * @param segmentId ID of the segment containing the paragraph
   * @param paragraphId ID of the paragraph to delete
   * @param userId ID of the user making the change
   * @returns True if the paragraph was deleted successfully
   */
  deleteParagraph(
    segmentId: string,
    paragraphId: string,
    userId: string
  ): boolean {
    if (!this.container) return false;
    
    const segment = this.container.getSegment(segmentId);
    if (!segment) return false;
    
    const paragraph = segment.getParagraph(paragraphId);
    if (!paragraph) return false;
    
    // Create and execute command to delete the paragraph
    const command = new DeleteParagraphCommand(
      this,
      segmentId,
      paragraphId,
      userId
    );
    
    // Add command to command manager for undo/redo
    // Now we return the Promise from execute instead of a direct boolean
    this.commandManager.execute(command);
    return true;
  }
   
  /**
   * Assigns a speaker to a specific element
   * This may involve complex operations like paragraph splitting and segment creation
   * 
   * @param segmentId ID of the segment containing the element
   * @param paragraphId ID of the paragraph containing the element
   * @param elementId ID of the element to assign speaker to
   * @param speakerLabel Label of the speaker to assign
   * @param datoSpeakerId Dato CMS ID of the speaker
   * @param userId ID of the user making the change
   * @returns True if the operation succeeded
   */
  assignElementSpeaker(
    segmentId: string,
    paragraphId: string,
    elementId: string,
    speakerLabel: string,
    datoSpeakerId: string,
    userId: string
  ): boolean {
    try {
      // Use our command class to handle the operation consistently
      const { AssignElementSpeakerCommand } = require('./commands/AssignElementSpeakerCommand');
      
      const command = new AssignElementSpeakerCommand(
        this,
        segmentId,
        paragraphId,
        elementId,
        speakerLabel,
        datoSpeakerId,
        userId
      );
      
      // Execute the command through the command manager
      // This ensures proper undo/redo tracking
      this.commandManager.execute(command);
      
      return true;
    } catch (error) {
      console.error('Error assigning element speaker:', error);
      return false;
    }
  }

  /**
   * Gets all text for a specific speaker
   * 
   * @param speakerIdentifier The identifier of the speaker to extract text for (can be transcript ID or DatoCMS ID)
   * @returns A string containing all text spoken by the specified speaker
   */
  getTextBySpeaker(speakerIdentifier: string): string {
    if (!this.container || !speakerIdentifier) {
      return '';
    }
    
    // Use the speaker label directly as we don't have access to the speaker service
    let transcriptSpeakerLabel = speakerIdentifier;
    
    // If we have speaker mappings, try to use them
    const speakerMappings = this.getSpeakerMappings();
    if (speakerMappings && speakerMappings.mappings) {
      // Find if this ID is a DatoCMS ID in the values of the mappings
      const entry = Object.entries(speakerMappings.mappings).find(([_, datoId]) => datoId === speakerIdentifier);
      if (entry) {
        // If found, use the transcript speaker label instead
        transcriptSpeakerLabel = entry[0];
      }
    }
    
    // Get all segments for the speaker
    const speakerSegments = this.container.getSegmentsBySpeaker(transcriptSpeakerLabel);
    
    // Extract text from each segment's paragraphs
    const speakerTexts = speakerSegments.map(segment => {
      return segment.paragraphs.map(paragraph => {
        return paragraph.getText();
      }).join(' ');
    });
    
    return speakerTexts.join(' ').trim();
  }


  /**
   * Assign a speaker to the current selection (segment or elements)
   * @param speakerLabel The transcript speaker label (e.g., "spk_0")
   * @param datoSpeakerId The DatoCMS speaker ID
   * @param userId The ID of the user making the change
   * @returns Promise resolving to true if successful
   */
  async assignSpeakerToCurrentSelection(
    speakerLabel: string | null, 
    datoSpeakerId: string | null,
    userId: string
  ): Promise<boolean> {
 
    if (!this.commandManager) {
      console.warn('Cannot assign speaker: command manager not available');
      return false;
    }
    
    // Get the current selection type from selection service
    const selectionType = this.selectionService?.currentSelectionType;
    let command: any = null;

    if (selectionType === 'segment') {
      // Create command for segment selection
      command = new AssignSpeakerToSegmentCommand(
        this,
        this.selectionService?.selectedSegment,
        speakerLabel,
        datoSpeakerId,
        userId
      );
    } else if (selectionType === 'elements') {
      // Create command for element selection
      command = new AssignSpeakerToSelectedTextCommand(
        this,
        this.selectionService?.getSelectedElements(),
        speakerLabel,
        datoSpeakerId,
        userId
      );
    }

    if (command) {
      try {
        // Execute the command through the command manager for proper tracking and undo/redo
        const result = await this.commandManager.execute(command);

        return result;
      } catch (error) {
        console.error('Error executing speaker assignment command:', error);
        return false;
      }
    }
    return false;
  }

  // ============================================================================
  // Color Management Methods
  // ============================================================================

  /**
   * Assigns a color to multiple elements
   * @param elementIds Array of element IDs to assign color to
   * @param color Hex color code to assign
   * @param userId ID of the user making the change
   * @returns Promise resolving to true if successful
   */
  async assignColorToElements(elementIds: string[], color: string, userId: string): Promise<boolean> {
    if (!elementIds.length || !color) return false;

    const command = new TranscriptAssignColorCommand(
      this,
      elementIds,
      color,
      userId
    );

    return await this.commandManager.execute(command);
  }

  /**
   * Clears color from multiple elements
   * @param elementIds Array of element IDs to clear color from
   * @param userId ID of the user making the change
   * @returns Promise resolving to true if successful
   */
  async clearColorFromElements(elementIds: string[], userId: string): Promise<boolean> {
    if (!elementIds.length) return false;

    const command = new TranscriptClearColorCommand(
      this,
      elementIds,
      userId
    );

    return await this.commandManager.execute(command);
  }

  /**
   * Gets all elements with a specific color
   * @param color Hex color code to search for
   * @returns Array of elements with the specified color
   */
  getElementsWithColor(color: string): ITranscriptElement[] {
    const elements: ITranscriptElement[] = [];
    const container = this.getContainer();
    
    if (!container) return elements;

    for (const segment of container.segments) {
      for (const paragraph of segment.paragraphs) {
        for (const element of paragraph.elements) {
          if (element.color === color) {
            elements.push(element);
          }
        }
      }
    }

    return elements;
  }

  /**
   * Gets all unique colors used in the transcript
   * @returns Array of unique color codes
   */
  getUsedColors(): string[] {
    const colors = new Set<string>();
    const container = this.getContainer();
    
    if (!container) return [];

    for (const segment of container.segments) {
      for (const paragraph of segment.paragraphs) {
        for (const element of paragraph.elements) {
          if (element.color) {
            colors.add(element.color);
          }
        }
      }
    }

    return Array.from(colors);
  }

  /**
   * Finds the next element with a specific color after a given time
   * @param color Hex color code to search for
   * @param afterTime Time in seconds to search after (defaults to 0)
   * @returns The next element with the color, or null if none found
   */
  findNextElementWithColor(color: string, afterTime: number = 0): ITranscriptElement | null {
    const container = this.getContainer();
    if (!container) return null;

    let closestElement: ITranscriptElement | null = null;
    let closestTime = Infinity;

    for (const segment of container.segments) {
      for (const paragraph of segment.paragraphs) {
        for (const element of paragraph.elements) {
          if (element.color === color && element.type === 'text') {
            const textElement = element as ITextElement;
            const elementTime = textElement.startTime || 0;
            
            if (elementTime > afterTime && elementTime < closestTime) {
              closestElement = element;
              closestTime = elementTime;
            }
          }
        }
      }
    }

    // If no element found after the given time, wrap around to find the first one
    if (!closestElement) {
      for (const segment of container.segments) {
        for (const paragraph of segment.paragraphs) {
          for (const element of paragraph.elements) {
            if (element.color === color && element.type === 'text') {
              const textElement = element as ITextElement;
              const elementTime = textElement.startTime || 0;
              
              if (elementTime < closestTime) {
                closestElement = element;
                closestTime = elementTime;
              }
            }
          }
        }
      }
    }

    return closestElement;
  }
}